import { useRef, useState } from 'react';
import { Copy, Check, ExternalLink, FileText } from 'lucide-react';
import { money } from '@generated/components/pricing';
import { notifyPaymentStarted } from '@generated/server/payNotice';
import { openReceipt } from '@generated/components/receipt';

/** kind = what is being paid for (shown in George's bell + report); who = customer / description. */
interface Props { method: string; orderId: string; dueNow: number; total: number; monthly: boolean; email?: string; kind?: string; who?: string }

const ZELLE = '917-652-9128';

export function PayPanel({ method, orderId, dueNow, total, monthly, email = '', kind = 'Payment', who }: Props) {
  const [copied, setCopied] = useState<string | null>(null);
  const memo = `Star-Revue order #${orderId}`;
  const amount = dueNow.toFixed(2);
  const sent = useRef(new Set<string>());
  const [zelleSent, setZelleSent] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState(email);
  const [started, setStarted] = useState(false);
  const payMethod = method.startsWith('Zelle') ? 'Zelle' : method;
  const notice = (m: string) => {
    setStarted(true);
    const key = `${m}|${dueNow}`; if (sent.current.has(key)) return; sent.current.add(key);
    notifyPaymentStarted({ data: { kind, what: who || `order #${orderId}`, amount: dueNow, method: m.startsWith('Zelle') ? 'Zelle' : m, email: receiptEmail.trim() || undefined, ref: orderId } })
      .catch((e) => console.error('[pay] notice failed', e));
  };
  const copy = async (key: string, text: string) => {
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; }
    catch {
      // Embedded pages often block the clipboard API — fall back to the classic copy command.
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); ok = document.execCommand('copy'); document.body.removeChild(ta);
      } catch (e) { console.error(e); }
    }
    setCopied(ok ? key : `fail-${key}`);
    setTimeout(() => setCopied(null), 2500);
  };
  const CopyBtn = ({ k, text }: { k: string; text: string }) => (
<span className="inline-flex items-center">
        <button type="button" onClick={() => copy(k, text)} aria-label={`Copy ${k}`} className="ml-2 inline-flex h-8 w-8 items-center justify-center border border-border hover:border-foreground">
      {copied === k ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
    {copied === k && <span className="ml-1 text-xs text-primary">Copied</span>}
    {copied === `fail-${k}` && <span className="ml-1 text-xs text-muted-foreground">Select &amp; copy manually</span>}
    </span>
  );

  return (
    <section onClickCapture={(e) => { if ((e.target as HTMLElement).closest("a[target]")) notice(method); }} className="mt-8 border-2 border-[hsl(var(--brand-blue))] bg-card p-5 print:hidden">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--brand-blue))]">Complete your payment</div>
      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">{money(dueNow)} due now</h3>
        {monthly && <span className="text-xs text-muted-foreground">First month of {money(total)} · remaining months billed monthly</span>}
      </div>

      {method.startsWith('Zelle') && (
        <div className="mt-4 space-y-2 text-sm">
          <p>Open your bank's app, choose <strong>Zelle</strong>, and send:</p>
          <dl className="divide-y divide-border border-y border-border">
            <div className="flex items-center justify-between py-2"><dt className="text-muted-foreground">To</dt><dd className="flex select-all items-center font-medium">{ZELLE} · George Fiala<CopyBtn k="number" text={ZELLE} /></dd></div>
            <div className="flex items-center justify-between py-2"><dt className="text-muted-foreground">Amount</dt><dd className="flex select-all items-center font-medium tabular-nums">{money(dueNow)}<CopyBtn k="amount" text={amount} /></dd></div>
            <div className="flex items-center justify-between py-2"><dt className="text-muted-foreground">Memo</dt><dd className="flex select-all items-center font-medium">{memo}<CopyBtn k="memo" text={memo} /></dd></div>
          </dl>
          <button type="button" disabled={zelleSent} onClick={() => { notice("Zelle"); setZelleSent(true); }} className="mt-2 inline-flex min-h-11 items-center gap-2 border-2 border-foreground px-4 text-sm font-semibold disabled:opacity-60">{zelleSent ? <><Check className="h-4 w-4" />Thanks, we'll watch for it</> : "I've sent my Zelle payment"}</button>
        </div>
      )}

      {method === 'Venmo' && (
        <div className="mt-4 space-y-3 text-sm">
          <p>Pay with Venmo to <strong>@George-Fiala</strong>. The amount and note are filled in for you.</p>
          <a href={`https://venmo.com/George-Fiala?txn=pay&amount=${amount}&note=${encodeURIComponent(memo)}`} target="_blank" rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-blue))] px-6 text-sm font-semibold text-primary-foreground">
            Pay {money(dueNow)} with Venmo<ExternalLink className="h-4 w-4" />
          </a>
          <p className="text-xs text-muted-foreground">On a phone this opens the Venmo app. Please check the recipient shows George Fiala before you pay.</p>
        </div>
      )}

      {method === 'PayPal' && (
        <div className="mt-4 space-y-3 text-sm">
          <p>Pay securely with PayPal. The amount is filled in for you; please add <strong>{memo}</strong> as the note.</p>
          <a href={`https://paypal.me/Starrevue/${amount}USD`} target="_blank" rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-blue))] px-6 text-sm font-semibold text-primary-foreground">
            Pay {money(dueNow)} with PayPal<ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}

      {method === 'Credit Card' && (
        <div className="mt-4 space-y-3 text-sm">
          <p>Pay securely by card through Stripe. The card page can't fill in the amount for you, so please <strong>type this amount</strong> into its "Amount" box:</p>
          <div className="flex flex-wrap items-center gap-2 border-2 border-foreground bg-[hsl(var(--brand-yellow))]/40 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-wider">Amount to enter</span>
            <span className="select-all font-[family-name:var(--font-heading)] text-3xl font-bold tabular-nums">{amount}</span>
            <CopyBtn k="card amount" text={amount} />
          </div>
          <a href={`https://buy.stripe.com/00w14f7iEfvC1uK7AW4wM00?client_reference_id=${encodeURIComponent(orderId)}${email ? `&prefilled_email=${encodeURIComponent(email)}` : ''}`} target="_blank" rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-blue))] px-6 text-sm font-semibold text-primary-foreground">
            Pay {money(dueNow)} by card<ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}

      <div className="mt-5 border-t border-border pt-4 text-sm">
        <label htmlFor={`rcpt-${orderId}`} className="text-xs font-semibold uppercase tracking-wider">Want a receipt? Add your email address</label>
        <input id={`rcpt-${orderId}`} type="email" value={receiptEmail} onChange={(e) => setReceiptEmail(e.target.value)} placeholder="you@example.com"
          className="mt-1 block h-11 w-full max-w-sm border border-input bg-background px-3 text-sm" />
        <p className="mt-1 text-xs text-muted-foreground">Add it before you pay, and we'll email a Star-Revue receipt once your payment arrives.</p>
        {started && <button type="button" onClick={() => openReceipt({ kind, who, ref: orderId, amount: dueNow, method: payMethod })}
          className="mt-3 inline-flex min-h-11 items-center gap-2 border-2 border-foreground px-4 text-sm font-semibold"><FileText className="h-4 w-4" />Print or save my receipt</button>}
      </div>
    </section>
  );
}
